import { type RefObject, useState } from 'react'
import html2canvas from 'html2canvas-pro'
import { jsPDF } from 'jspdf'
import pptxgen from 'pptxgenjs'
import { toast } from '@/stores/useToastStore'

const PPTX_BASE_WIDTH = 13.333
const PPTX_MAX_DIMENSION = 56
const MAX_CANVAS_DIMENSION = 16_384

function getPptxDimensions(canvas: HTMLCanvasElement) {
  let width = PPTX_BASE_WIDTH
  let height = width * (canvas.height / canvas.width)

  if (height > PPTX_MAX_DIMENSION) {
    height = PPTX_MAX_DIMENSION
    width = height * (canvas.width / canvas.height)
  }

  return { width, height }
}

export function useDashboardExport(
  contentRef: RefObject<HTMLElement | null>,
  filename: string
) {
  const [isExporting, setIsExporting] = useState(false)

  const captureCanvas = async () => {
    const el = contentRef.current
    if (!el) throw new Error('Nothing to export')

    await document.fonts?.ready

    const bounds = el.getBoundingClientRect()
    const width = Math.ceil(bounds.width)
    const height = Math.ceil(Math.max(bounds.height, el.scrollHeight))
    const longestSide = Math.max(width, height)
    const scale = Math.min(2, MAX_CANVAS_DIMENSION / longestSide)

    return html2canvas(el, {
      scale,
      useCORS: true,
      logging: false,
      allowTaint: false,
      backgroundColor: '#ffffff',
      width,
      height,
    })
  }

  const exportAsPdf = async () => {
    setIsExporting(true)
    try {
      const canvas = await captureCanvas()
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: canvas.width >= canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height],
        hotfixes: ['px_scaling'],
      })
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height)
      pdf.save(`${filename}.pdf`)
    } catch (error) {
      console.error('Failed to export dashboard as PDF', error)
      toast.error('Failed to export PDF')
    } finally {
      setIsExporting(false)
    }
  }

  const exportAsPpt = async () => {
    setIsExporting(true)
    try {
      const canvas = await captureCanvas()
      const imgData = canvas.toDataURL('image/png')
      const pptx = new pptxgen()
      const { width, height } = getPptxDimensions(canvas)
      pptx.defineLayout({ name: 'DASHBOARD', width, height })
      pptx.layout = 'DASHBOARD'
      const slide = pptx.addSlide()
      slide.background = { color: 'FFFFFF' }
      slide.addImage({
        data: imgData,
        x: 0,
        y: 0,
        w: width,
        h: height,
      })
      await pptx.writeFile({ fileName: `${filename}.pptx` })
    } catch (error) {
      console.error('Failed to export dashboard as PowerPoint', error)
      toast.error('Failed to export PowerPoint')
    } finally {
      setIsExporting(false)
    }
  }

  return { exportAsPdf, exportAsPpt, isExporting }
}
